<template>
    <!--
        Actions dropdown — redesign of the legacy header "Actions" menu
        (View deals · Add deal · View sales · Add legacy deal). Subtle
        button + rotating chevron, popover with per-item icon, hint text,
        and right-side meta (deal counts / YTD totals / ⌘ shortcuts).
        Permission-gated items (Add legacy deal) come from the parent
        already filtered, below a divider.

        Closes on click-outside or Escape.
    -->
    <div ref="root" :style="rootStyle">
        <button
            type="button"
            :class="btnClass"
            :style="btnStyle"
            :aria-expanded="open ? 'true' : 'false'"
            aria-haspopup="menu"
            @click="open = !open"
        >
            Actions
            <span :style="chevStyle"><Icon name="chevDown" :size="12" /></span>
        </button>

        <div v-if="open" role="menu" :style="menuStyle">
            <template v-for="(a, i) in actions">
                <div v-if="a.divider" :key="'d' + i" :style="dividerStyle" />
                <a
                    v-else
                    :key="a.id"
                    role="menuitem"
                    :href="a.href || '#'"
                    :style="itemStyle(a)"
                    @click="onItemClick(a, $event)"
                    @mouseenter="hover($event, true)"
                    @mouseleave="hover($event, false)"
                >
                    <span :style="itemIconStyle(a)"><Icon :name="a.icon" :size="15" /></span>
                    <span :style="itemBodyStyle">
                        <span :style="itemLabelStyle(a)">{{ a.label }}</span>
                        <span v-if="a.hint" :style="itemHintStyle">{{ a.hint }}</span>
                    </span>
                    <span v-if="a.meta" class="tag-mono tag-tnum" :style="itemMetaStyle">{{ a.meta }}</span>
                    <span v-else-if="a.kbd && a.kbd.length" :style="kbdGroupStyle">
                        <span v-for="(k, j) in a.kbd" :key="j" class="kbd">{{ k }}</span>
                    </span>
                </a>
            </template>
        </div>
    </div>
</template>

<script>
import Icon from "./Icon.vue";

export default {
    name: "ActionsMenu",
    components: { Icon },
    props: {
        // [{ id, label, icon, href, meta, hint, accent, muted, kbd:[], divider }]
        actions: { type: Array, default: () => [] },
        align: { type: String, default: "right", validator: (v) => ["left", "right"].includes(v) },
        variant: { type: String, default: "subtle", validator: (v) => ["subtle", "ghost"].includes(v) },
        defaultOpen: { type: Boolean, default: false },
    },
    data() {
        return { open: this.defaultOpen };
    },
    computed: {
        btnClass() {
            return this.variant === "ghost" ? "btn btn--ghost btn--sm" : "btn btn--subtle btn--sm";
        },
        rootStyle() {
            return { position: "relative", display: "inline-flex" };
        },
        btnStyle() {
            const base = { gap: "8px" };
            if (this.open) {
                return {
                    ...base,
                    background: "var(--hover-strong)",
                    borderColor: "var(--border-strong)",
                    color: "var(--text)",
                };
            }
            return base;
        },
        chevStyle() {
            return {
                color: "var(--text-faint)",
                transition: "transform .12s",
                transform: this.open ? "rotate(180deg)" : "rotate(0)",
                display: "inline-flex",
            };
        },
        menuStyle() {
            return {
                position: "absolute",
                top: "calc(100% + 6px)",
                [this.align]: 0,
                minWidth: "268px",
                background: "var(--surface)",
                border: "1px solid var(--border)",
                borderRadius: "var(--r-md)",
                boxShadow: "var(--shadow-pop)",
                padding: "4px",
                zIndex: 30,
                fontSize: "13px",
            };
        },
        dividerStyle() {
            return { height: "1px", background: "var(--border-faint)", margin: "4px 6px" };
        },
        itemBodyStyle() {
            return { display: "flex", flexDirection: "column", gap: "1px", minWidth: 0 };
        },
        itemHintStyle() {
            return { fontSize: "11.5px", color: "var(--text-faint)", fontWeight: 400 };
        },
        itemMetaStyle() {
            return { fontSize: "11px", color: "var(--text-faint)", fontWeight: 500 };
        },
        kbdGroupStyle() {
            return { display: "inline-flex", gap: "2px" };
        },
    },
    mounted() {
        if (this.open) this.bind();
        this.$watch("open", (v) => (v ? this.bind() : this.unbind()));
    },
    beforeDestroy() {
        this.unbind();
    },
    methods: {
        bind() {
            this.__onDown = (e) => {
                if (this.$refs.root && !this.$refs.root.contains(e.target)) this.open = false;
            };
            this.__onKey = (e) => {
                if (e.key === "Escape") this.open = false;
            };
            document.addEventListener("mousedown", this.__onDown);
            document.addEventListener("keydown", this.__onKey);
        },
        unbind() {
            if (this.__onDown) document.removeEventListener("mousedown", this.__onDown);
            if (this.__onKey) document.removeEventListener("keydown", this.__onKey);
            this.__onDown = null;
            this.__onKey = null;
        },
        onItemClick(a, e) {
            // Items with a real href navigate normally; placeholders (#)
            // just close the menu. Emit `select` so the host can hook in.
            if (!a.href || a.href === "#") e.preventDefault();
            this.open = false;
            this.$emit("select", a);
        },
        itemStyle(a) {
            return {
                display: "grid",
                gridTemplateColumns: "20px 1fr auto",
                gap: "10px",
                alignItems: "center",
                padding: "7px 8px 7px 10px",
                borderRadius: "var(--r-sm)",
                color: a.muted ? "var(--text-muted)" : "var(--text)",
                textDecoration: "none",
                cursor: "pointer",
                lineHeight: 1.3,
            };
        },
        itemIconStyle(a) {
            return {
                color: a.accent ? "var(--tag-red)" : a.muted ? "var(--text-faint)" : "var(--text-muted)",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
            };
        },
        itemLabelStyle(a) {
            return { fontWeight: 500, color: a.accent ? "var(--tag-red)" : "inherit" };
        },
        hover(e, on) {
            e.currentTarget.style.background = on ? "var(--hover)" : "transparent";
        },
    },
};
</script>
